/**
 * Company Details Page
 * Displays all companies in a card layout with search and management features
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Card, Input, Button, Space, Tag, message, Popconfirm, Tooltip, Badge, Divider } from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  SettingOutlined,
  DeleteFilled,
  UserOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  SafetyOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  DownloadOutlined,
  ApartmentOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import superAdminService, { Company, CompanyStats } from '../services/superAdminService';
import { RBACService } from '../utils/rbac';
import { useResponsive } from '../hooks/useResponsive';
import SuperAdminSidebar from '../components/superadmin/SuperAdminSidebar';
import SuperAdminHeader from '../components/superadmin/SuperAdminHeader';
import CompanyDetailsModal from '../components/superadmin/CompanyDetailsModal';
import EditCompanyModal from '../components/superadmin/EditCompanyModal';
import SubscriptionModal from '../components/superadmin/SubscriptionModal';
import './CompanyDetails.css';

const { Content } = Layout;
const { Search } = Input;

const CompanyDetails: React.FC = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);

  useEffect(() => {
    if (isMobile) setSidebarCollapsed(true);
  }, [isMobile]);
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [companyStats, setCompanyStats] = useState<CompanyStats | null>(null);
  
  // Modal states
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [subscriptionModalVisible, setSubscriptionModalVisible] = useState(false);

  // Dummy companies data for testing
  const dummyCompanies: Company[] = [
    {
      id: 1,
      name: 'Bhutan Tech Solutions',
      code: 'C001',
      description: 'Leading technology solutions provider in Bhutan',
      email: 'info@bhutantech.bt',
      phone: '+975-2-123456',
      address: 'Thimphu, Bhutan',
      country: 'Bhutan',
      corporate_residence: 'Thimphu, Bhutan',
      registration_number: 'BT-2024-001',
      tax_id: 'TAX-001',
      plan_type: 'premium',
      max_users: 15,
      max_locations: 3,
      license_type: 'macro',
      is_active: true,
      subscription_expires_at: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
      created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: new Date().toISOString(),
    },
    {
      id: 2,
      name: 'Druk Enterprises',
      code: 'C002',
      description: 'Business solutions and consulting services',
      email: 'contact@drukenterprises.bt',
      phone: '+975-2-234567',
      address: 'Paro, Bhutan',
      country: 'Bhutan',
      corporate_residence: 'Paro, Bhutan',
      registration_number: 'BT-2024-002',
      tax_id: 'TAX-002',
      plan_type: 'basic',
      max_users: 5,
      max_locations: 1,
      license_type: 'micro',
      is_active: true,
      subscription_expires_at: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
      created_at: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: new Date().toISOString(),
    },
    {
      id: 3,
      name: 'Himalayan Trading Co.',
      code: 'C003',
      description: 'Import and export trading company',
      email: 'info@himalayantrading.bt',
      phone: '+975-2-345678',
      address: 'Phuentsholing, Bhutan',
      country: 'Bhutan',
      corporate_residence: 'Phuentsholing, Bhutan',
      registration_number: 'BT-2024-003',
      tax_id: 'TAX-003',
      plan_type: 'enterprise',
      max_users: 50,
      max_locations: 10,
      license_type: 'macro',
      is_active: true,
      subscription_expires_at: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString(),
      created_at: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: new Date().toISOString(),
    },
    {
      id: 4,
      name: 'Bhutan Retail Group',
      code: 'C004',
      description: 'Retail chain management and operations',
      email: 'admin@bhutanretail.bt',
      phone: '+975-2-456789',
      address: 'Thimphu, Bhutan',
      country: 'Bhutan',
      corporate_residence: 'Thimphu, Bhutan',
      registration_number: 'BT-2024-004',
      tax_id: 'TAX-004',
      plan_type: 'premium',
      max_users: 25,
      max_locations: 5,
      license_type: 'macro',
      is_active: true,
      subscription_expires_at: new Date(Date.now() + 120 * 24 * 60 * 60 * 1000).toISOString(),
      created_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: new Date().toISOString(),
    },
    {
      id: 5,
      name: 'Mountain View Services',
      code: 'C005',
      description: 'Professional services and consulting',
      email: 'contact@mountainview.bt',
      phone: '+975-2-567890',
      address: 'Bumthang, Bhutan',
      country: 'Bhutan',
      corporate_residence: 'Bumthang, Bhutan',
      registration_number: 'BT-2024-005',
      tax_id: 'TAX-005',
      plan_type: 'basic',
      max_users: 8,
      max_locations: 2,
      license_type: 'micro',
      is_active: false,
      subscription_expires_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
      created_at: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: new Date().toISOString(),
    },
    {
      id: 6,
      name: 'Dragon Digital Solutions',
      code: 'C006',
      description: 'Digital transformation and IT services',
      email: 'info@dragondigital.bt',
      phone: '+975-2-678901',
      address: 'Thimphu, Bhutan',
      country: 'Bhutan',
      corporate_residence: 'Thimphu, Bhutan',
      registration_number: 'BT-2024-006',
      tax_id: 'TAX-006',
      plan_type: 'enterprise',
      max_users: 100,
      max_locations: 15,
      license_type: 'macro',
      is_active: true,
      subscription_expires_at: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
      created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: new Date().toISOString(),
    },
  ];

  // Load companies
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const data = await superAdminService.getCompanies();
      setCompanies(data || []);
      setFilteredCompanies(data || []);
    } catch (error: any) {
      console.error('Failed to load companies from API:', error);
      message.error('Failed to load companies. Please check your connection and try again.');
      setCompanies([]);
      setFilteredCompanies([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  // Filter companies based on search
  useEffect(() => {
    if (!searchText.trim()) {
      setFilteredCompanies(companies);
    } else {
      const filtered = companies.filter(
        (company) =>
          company.name.toLowerCase().includes(searchText.toLowerCase()) ||
          company.code.toLowerCase().includes(searchText.toLowerCase()) ||
          company.country?.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredCompanies(filtered);
    }
  }, [searchText, companies]);

  // Load company stats
  const loadCompanyStats = async (companyId: number) => {
    try {
      const stats = await superAdminService.getCompanyStats(companyId);
      setCompanyStats(stats);
    } catch (error: any) {
      message.error('Failed to load company statistics');
      console.error('Error loading stats:', error);
    }
  };

  // Handle view company details
  const handleView = async (company: Company) => {
    setSelectedCompany(company);
    await loadCompanyStats(company.id);
    setDetailsModalVisible(true);
  };

  // Handle edit company
  const handleEdit = (company: Company) => {
    setSelectedCompany(company);
    setEditModalVisible(true);
  };

  const handleEditSuccess = () => {
    setEditModalVisible(false);
    setSelectedCompany(null);
    loadCompanies();
    message.success('Company updated successfully!');
  };

  // Handle subscription update
  const handleSubscription = (company: Company) => {
    setSelectedCompany(company);
    setSubscriptionModalVisible(true);
  };

  const handleSubscriptionSuccess = () => {
    setSubscriptionModalVisible(false);
    loadCompanies();
    message.success('Subscription updated successfully!');
  };

  // Handle delete company
  const handleDelete = async (companyId: number) => {
    try {
      await superAdminService.deleteCompany(companyId);
      message.success('Company deleted successfully');
      loadCompanies();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete company');
    }
  };

  // Handle create company
  const handleCreateCompany = () => {
    navigate('/superadmin/add-company');
  };

  // Handle export to CSV
  const handleExport = () => {
    try {
      const headers = [
        'ID',
        'Name',
        'Code',
        'Email',
        'Phone',
        'Country',
        'Plan Type',
        'License Type',
        'Max Users',
        'Max Locations',
        'Status',
        'Subscription Expires At',
      ];

      const rows = filteredCompanies.map(company => [
        company.id,
        company.name,
        company.code,
        company.email || '',
        company.phone || '',
        company.country || '',
        company.plan_type || 'basic',
        company.license_type || '',
        company.max_users,
        company.max_locations,
        company.is_active ? 'Active' : 'Inactive',
        company.subscription_expires_at ? new Date(company.subscription_expires_at).toLocaleDateString() : 'No expiration',
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `companies_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      message.success('Companies exported successfully!');
    } catch (error: any) {
      message.error('Failed to export companies');
      console.error('Export error:', error);
    }
  };

  // Get subscription status
  const getSubscriptionStatus = (company: Company) => {
    if (!company.subscription_expires_at) {
      return { status: 'success', text: 'No Expiration', icon: <CheckCircleOutlined /> };
    }
    const expiryDate = new Date(company.subscription_expires_at);
    const now = new Date();
    const daysRemaining = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysRemaining < 0) {
      return { status: 'error', text: 'Expired', icon: <CloseCircleOutlined /> };
    } else if (daysRemaining < 30) {
      return { status: 'warning', text: `${daysRemaining} days left`, icon: <CalendarOutlined /> };
    } else {
      return { status: 'success', text: `${daysRemaining} days left`, icon: <CheckCircleOutlined /> };
    }
  };

  const handleMenuClick = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <Layout className={`superadmin-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`} style={{ minHeight: '100dvh', background: '#f0f2f5', display: 'flex', flexDirection: 'row', overflowX: 'hidden', maxWidth: '100vw' }}>
      <SuperAdminHeader 
        onMenuClick={handleMenuClick}
        sidebarCollapsed={sidebarCollapsed}
        isMobile={isMobile}
      />
      <SuperAdminSidebar 
        collapsed={sidebarCollapsed}
        onCollapse={setSidebarCollapsed}
        onCreateCompany={handleCreateCompany}
        user={{
          name: RBACService.getCurrentUser()?.name || 'Super Admin',
          email: RBACService.getCurrentUser()?.email || 'admin@system.bt'
        }}
      />
      <div
        className="superadmin-content-wrapper"
        style={{
          flex: 1,
          minWidth: 0,
          marginLeft: isMobile ? 0 : (sidebarCollapsed ? 80 : 280),
          marginTop: isMobile ? 64 : 80,
          transition: 'margin-left 0.3s ease, width 0.3s ease',
          width: isMobile ? '100%' : `calc(100vw - ${sidebarCollapsed ? 80 : 280}px)`,
          maxWidth: isMobile ? '100%' : `calc(100vw - ${sidebarCollapsed ? 80 : 280}px)`,
          minHeight: isMobile ? 'calc(100dvh - 64px)' : 'calc(100dvh - 80px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
      <Content 
        className="main-content-full superadmin-content"
        style={{ 
          flex: 1,
          minWidth: 0,
          margin: 0,
          padding: isMobile ? 12 : 24,
          background: '#ffffff',
          overflow: 'auto',
          minHeight: 0,
        }}
      >
        <div style={{ width: '100%', maxWidth: '1400px', margin: '0 auto' }}>
          {/* Header Section */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 16 }}>
              <div>
                <h1 style={{ fontSize: 28, fontWeight: 600, color: '#1a1a1a', margin: 0, marginBottom: 8 }}>
                  Company Management
                </h1>
                <p style={{ fontSize: 14, color: '#8c8c8c', margin: 0 }}>
                  View and manage all registered companies
                </p>
              </div>
              <Space size="middle" wrap>
                <Search
                  className="app-search"
                  placeholder="Search companies..."
                  allowClear
                  enterButton={<Button type="primary" icon={<SearchOutlined />}>Search</Button>}
                  size="large"
                  style={{ width: isMobile ? '100%' : 400 }}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onSearch={(value) => setSearchText(value)}
                />
                <Tooltip title="Export to CSV">
                  <Button
                    icon={<DownloadOutlined />}
                    size="large"
                    onClick={handleExport}
                  >
                    Export
                  </Button>
                </Tooltip>
              </Space>
            </div>
          </div>

          {/* Companies Grid */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div>Loading companies...</div>
            </div>
          ) : filteredCompanies.length === 0 ? (
            <Card style={{ textAlign: 'center', padding: '60px 20px' }}>
              <p style={{ fontSize: 16, color: '#8c8c8c', margin: 0 }}>
                {searchText ? 'No companies found matching your search.' : 'No companies registered yet.'}
              </p>
            </Card>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
              {filteredCompanies.map((company) => (
                <Card
                  key={company.id}
                  style={{
                    borderRadius: 8,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    border: '1px solid #e8e8e8',
                    transition: 'all 0.3s ease',
                    width: '100%',
                  }}
                  hoverable
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.12)';
                    e.currentTarget.style.borderColor = '#1890ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                    e.currentTarget.style.borderColor = '#e8e8e8';
                  }}
                >
                  <div className={isMobile ? 'company-card-inner company-card-inner--mobile' : 'company-card-inner'} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 24, minHeight: 100 }}>
                    {/* Left Section - Company Info */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <ApartmentOutlined style={{ color: '#1890ff', fontSize: 24 }} />
                          <h3 
                            style={{ 
                              fontSize: 20, 
                              fontWeight: 600, 
                              color: '#1a1a1a', 
                              margin: 0,
                              cursor: 'pointer',
                              transition: 'color 0.3s'
                            }}
                            onClick={() => handleView(company)}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#1890ff'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#1a1a1a'}
                          >
                            {company.name}
                          </h3>
                        </div>
                        <Space>
                          <Badge 
                            status={company.is_active ? 'success' : 'error'} 
                            text={
                              <Tag color={company.is_active ? 'green' : 'red'} icon={company.is_active ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                                {company.is_active ? 'Active' : 'Inactive'}
                              </Tag>
                            }
                          />
                          {company.license_type && (
                            <Tag color="purple" icon={<GlobalOutlined />}>
                              {company.license_type === 'macro' ? 'Macro' : 'Micro'}
                            </Tag>
                          )}
                        </Space>
                      </div>

                      {/* Company Details Row */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <IdcardOutlined style={{ color: '#1890ff', fontSize: 16 }} />
                          <span style={{ fontSize: 14, color: '#666', fontWeight: 500 }}>
                            ID: {company.code}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <UserOutlined style={{ color: '#9c27b0', fontSize: 16 }} />
                          <span style={{ fontSize: 14, color: '#666' }}>
                            {company.max_users} Users
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <EnvironmentOutlined style={{ color: '#9c27b0', fontSize: 16 }} />
                          <span style={{ fontSize: 14, color: '#666' }}>
                            {company.max_locations} Locations
                          </span>
                        </div>
                        {company.country && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <SafetyOutlined style={{ color: '#1890ff', fontSize: 16 }} />
                            <span style={{ fontSize: 14, color: '#666' }}>
                              {company.country}
                            </span>
                          </div>
                        )}
                        {company.email && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <MailOutlined style={{ color: '#1890ff', fontSize: 16 }} />
                            <a 
                              href={`mailto:${company.email}`}
                              style={{ fontSize: 14, color: '#1890ff', textDecoration: 'none' }}
                              onClick={(e) => e.stopPropagation()}
                              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                            >
                              {company.email}
                            </a>
                          </div>
                        )}
                        {company.phone && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <PhoneOutlined style={{ color: '#52c41a', fontSize: 16 }} />
                            <a 
                              href={`tel:${company.phone}`}
                              style={{ fontSize: 14, color: '#52c41a', textDecoration: 'none' }}
                              onClick={(e) => e.stopPropagation()}
                              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                            >
                              {company.phone}
                            </a>
                          </div>
                        )}
                      </div>

                      {/* Plan Type and Subscription Status */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                        {company.plan_type && (
                          <Tag color={company.plan_type === 'enterprise' ? 'purple' : company.plan_type === 'premium' ? 'gold' : 'blue'}>
                            {company.plan_type.toUpperCase()} Plan
                          </Tag>
                        )}
                        {company.subscription_expires_at && (
                          <Tag 
                            color={getSubscriptionStatus(company).status === 'error' ? 'red' : getSubscriptionStatus(company).status === 'warning' ? 'orange' : 'green'}
                            icon={getSubscriptionStatus(company).icon}
                          >
                            {getSubscriptionStatus(company).text}
                          </Tag>
                        )}
                      </div>
                    </div>

                    {/* Right Section - Action Buttons */}
                    <div
                      className="company-card-actions"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 8,
                        flexShrink: 0,
                        height: '100%',
                        minHeight: 80,
                        paddingLeft: 16,
                      }}
                    >
                      <Tooltip title="View Details">
                        <Button
                          className="company-action-btn company-action-btn--view"
                          type="text"
                          icon={<EyeOutlined />}
                          style={{ color: '#1890ff' }}
                          onClick={() => handleView(company)}
                        />
                      </Tooltip>
                      <Tooltip title="Edit Company">
                        <Button
                          className="company-action-btn company-action-btn--edit"
                          type="text"
                          icon={<EditOutlined />}
                          style={{ color: '#52c41a' }}
                          onClick={() => handleEdit(company)}
                        />
                      </Tooltip>
                      <Tooltip title="Subscription Settings">
                        <Button
                          className="company-action-btn company-action-btn--settings"
                          type="text"
                          icon={<SettingOutlined />}
                          style={{ color: '#fa8c16' }}
                          onClick={() => handleSubscription(company)}
                        />
                      </Tooltip>
                      <Divider type="vertical" style={{ height: 40, margin: '0 4px' }} />
                      <Popconfirm
                        title={
                          <div>
                            <div style={{ fontWeight: 'bold', color: '#8b0000', marginBottom: 4 }}>
                              Permanently Delete Company?
                            </div>
                            <div style={{ fontSize: 13 }}>
                              This will permanently delete <strong>{company.name}</strong> and ALL its data:
                              users, locations, and records. This cannot be undone.
                            </div>
                          </div>
                        }
                        onConfirm={() => handleDelete(company.id)}
                        okText="Yes, Delete Permanently"
                        okButtonProps={{ danger: true }}
                        cancelText="Cancel"
                      >
                        <Tooltip title="Delete">
                          <Button
                            className="company-action-btn company-action-btn--delete"
                            type="text"
                            icon={<DeleteFilled style={{ fontSize: 18, display: 'block' }} />}
                            style={{
                              margin: 0,
                              border: '2px solid #8b0000',
                              background: 'radial-gradient(circle, #ff4d4f 0%, #cc0000 100%)',
                              color: '#ffffff',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                            }}
                          />
                        </Tooltip>
                      </Popconfirm>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Modals */}
          <CompanyDetailsModal
            visible={detailsModalVisible}
            company={selectedCompany}
            stats={companyStats}
            sidebarCollapsed={sidebarCollapsed}
            onCancel={() => {
              setDetailsModalVisible(false);
              setSelectedCompany(null);
              setCompanyStats(null);
            }}
          />

          <EditCompanyModal
            visible={editModalVisible}
            company={selectedCompany}
            sidebarCollapsed={sidebarCollapsed}
            onCancel={() => {
              setEditModalVisible(false);
              setSelectedCompany(null);
            }}
            onSuccess={handleEditSuccess}
          />

          <SubscriptionModal
            visible={subscriptionModalVisible}
            company={selectedCompany}
            sidebarCollapsed={sidebarCollapsed}
            onCancel={() => setSubscriptionModalVisible(false)}
            onSuccess={handleSubscriptionSuccess}
          />
        </div>
      </Content>
      </div>
    </Layout>
  );
};

export default CompanyDetails;
